// Mission templates: list on the left, editor on the right. Steps have a title, hint, required flag and a
// binding to a console action (so they check off on their own); saving an existing template makes a new version.

import { useState } from 'react';
import { api, STEP_BINDINGS, type MissionTemplate, type MissionTemplateBody, type StepBinding, type TemplateStep } from '../../api.ts';
import { Icon } from '../../components/Icon.tsx';
import { Empty, ErrorBox, Loading } from '../../components/ui.tsx';
import { human } from '../../console/util.ts';
import { useLoad } from '../../hooks.ts';
import { href, navigate } from '../../router.ts';
import { fmtLocal } from '../../time.ts';
import { moveItem, SaveBar, UpDown } from './common.tsx';

const BINDING_TEXT: Record<StepBinding, string> = {
  video_review: 'Video review · checks off on Sync / alarm clip',
  call: 'Call · checks off on a call outcome',
  dispatch: 'Dispatch · checks off when dispatch is logged',
  note: 'Note · checks off when a note is added',
  talk_down: 'Talk-down (Phase 5; checked by hand until then)',
  relay: 'Deterrent relay (Phase 5; checked by hand until then)',
  manual: 'Manual · operator checks it',
};

function bodyOf(t: MissionTemplate | null): MissionTemplateBody {
  if (!t) return { name: '', vertical: null, eventTypes: ['default'], steps: [{ seq: 1, title: '', hint: null, required: true, binding: 'video_review' }] };
  return { name: t.name, vertical: t.vertical, eventTypes: t.eventTypes, steps: t.steps.slice().sort((a, b) => a.seq - b.seq) };
}

function Editor({ t, canEdit, onSaved }: { t: MissionTemplate | null; canEdit: boolean; onSaved: (t: MissionTemplate) => void }) {
  const initial = bodyOf(t);
  const [d, setD] = useState<MissionTemplateBody>(initial);
  const [types, setTypes] = useState(initial.eventTypes.join(', '));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  const cur: MissionTemplateBody = {
    ...d,
    eventTypes: types
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean),
    steps: d.steps.map((s, i) => ({ ...s, seq: i + 1 })),
  };
  const dirty = JSON.stringify(cur) !== JSON.stringify({ ...initial, steps: initial.steps.map((s, i) => ({ ...s, seq: i + 1 })) });
  const setStep = (i: number, p: Partial<TemplateStep>) => {
    setSaved(false);
    setD((o) => ({ ...o, steps: o.steps.map((s, j) => (j === i ? { ...s, ...p } : s)) }));
  };
  const invalid = !cur.name.trim() ? 'Give the template a name.' : cur.steps.length === 0 ? 'Add at least one step.' : cur.steps.some((s) => !s.title.trim()) ? 'Every step needs a title.' : null;

  const save = async () => {
    if (invalid) return setErr(new Error(invalid));
    setBusy(true);
    setErr(null);
    try {
      const body = { ...cur, name: cur.name.trim(), steps: cur.steps.map((s) => ({ ...s, title: s.title.trim(), hint: s.hint?.trim() || null })) };
      const r = t ? await api.updateMissionTemplate(t.id, body) : await api.createMissionTemplate(body);
      setSaved(true);
      onSaved(r);
    } catch (e) {
      setErr(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset disabled={!canEdit} className="tabbody">
      <div className="page-h">
        <div>
          <h2>{t ? t.name : 'New mission template'}</h2>
          {t && (
            <p>
              Version {t.version} · used by {t.siteCount} site{t.siteCount === 1 ? '' : 's'} · updated {fmtLocal(t.updatedAt)}
            </p>
          )}
        </div>
      </div>
      {t && <div className="banner"><Icon name="list" /><span>Saving makes version {t.version + 1}. Open events keep the version they started with; new events use the new one.</span></div>}
      <div className="fgrid">
        <label className="field">
          <span className="lbl">Name</span>
          <input value={d.name} onChange={(e) => (setSaved(false), setD({ ...d, name: e.target.value }))} />
        </label>
        <label className="field">
          <span className="lbl">Vertical</span>
          <input value={d.vertical ?? ''} onChange={(e) => (setSaved(false), setD({ ...d, vertical: e.target.value || null }))} className="mono" placeholder="all, auto_dealer, self_storage…" />
        </label>
        <label className="field">
          <span className="lbl">Event types (suggested)</span>
          <input value={types} onChange={(e) => (setSaved(false), setTypes(e.target.value))} className="mono" placeholder="default, person, vehicle" />
        </label>
      </div>
      <div className="lbl">Steps</div>
      {d.steps.map((s, i) => (
        <div key={i} className="sopstep tplstep">
          <UpDown i={i} n={d.steps.length} what="step" disabled={!canEdit} onMove={(dl) => setD((o) => ({ ...o, steps: moveItem(o.steps, i, dl) }))} />
          <span className="mono dim">{i + 1}</span>
          <div className="stepgrid">
            <input value={s.title} onChange={(e) => setStep(i, { title: e.target.value })} placeholder="Step title (what the operator does)" aria-label={`Step ${i + 1} title`} />
            <input value={s.hint ?? ''} onChange={(e) => setStep(i, { hint: e.target.value || null })} placeholder="Hint (optional)" aria-label={`Step ${i + 1} hint`} />
            <select value={s.binding} onChange={(e) => setStep(i, { binding: e.target.value as StepBinding })} aria-label={`Step ${i + 1} binding`}>
              {STEP_BINDINGS.map((b) => (
                <option key={b} value={b}>
                  {BINDING_TEXT[b]}
                </option>
              ))}
            </select>
            <label className="ck-inline">
              <input type="checkbox" checked={s.required} onChange={(e) => setStep(i, { required: e.target.checked })} />
              Required before closing
            </label>
          </div>
          <span className="row-wrap">
            <span className="typ">{human(s.binding)}</span>
            {canEdit && (
              <button type="button" className="btn icon sm" onClick={() => setD((o) => ({ ...o, steps: o.steps.filter((_, j) => j !== i) }))} aria-label={`Remove step ${i + 1}`}>
                <Icon name="trash" />
              </button>
            )}
          </span>
        </div>
      ))}
      {canEdit && (
        <button type="button" className="btn sm self-start" onClick={() => setD((o) => ({ ...o, steps: [...o.steps, { seq: o.steps.length + 1, title: '', hint: null, required: false, binding: 'manual' }] }))}>
          <Icon name="plus" />
          Add step
        </button>
      )}
      <SaveBar canEdit={canEdit} dirty={dirty || !t} busy={busy} error={err} saved={saved} onSave={() => void save()} onReset={() => (setD(initial), setTypes(initial.eventTypes.join(', ')))} label={t ? `Save as v${t.version + 1}` : 'Create template'} />
    </fieldset>
  );
}

export function MissionTemplatesEditor({ canEdit, selectedId }: { canEdit: boolean; selectedId: string | null }) {
  const load = useLoad((signal) => api.missionTemplates(signal), []);
  const list = (load.data ?? []).slice().sort((a, b) => a.name.localeCompare(b.name));
  const isNew = selectedId === 'new';
  const sel = isNew ? null : (list.find((t) => t.id === selectedId) ?? null);
  return (
    <div className="split2">
      <div className="card2 flush">
        <div className="card-h pad">
          <h3>
            <Icon name="list" />
            Mission templates
          </h3>
          {canEdit && (
            <div className="card-r">
              <a className="btn sm" href={href.setup('missions', 'new')}>
                <Icon name="plus" />
                New
              </a>
            </div>
          )}
        </div>
        {load.error && !load.data ? (
          <ErrorBox error={load.error} onRetry={load.reload} title="Could not load templates" />
        ) : !load.data ? (
          <Loading label="Loading templates…" />
        ) : list.length === 0 ? (
          <Empty>No templates yet.</Empty>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th scope="col">Template</th>
                  <th scope="col" className="num">Steps</th>
                  <th scope="col" className="num">Sites</th>
                  <th scope="col" className="num">Ver</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <tr key={t.id} className={`click${t.id === selectedId ? ' sel' : ''}`} onClick={() => navigate(href.setup('missions', t.id))} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && navigate(href.setup('missions', t.id))}>
                    <td>
                      <a href={href.setup('missions', t.id)} onClick={(e) => e.stopPropagation()}>
                        {t.name}
                      </a>
                      <div className="note">{human(t.vertical, 'all')} · {t.eventTypes.join(', ')}</div>
                    </td>
                    <td className="mono num">{t.steps.length}</td>
                    <td className="mono num">{t.siteCount}</td>
                    <td className="mono num">v{t.version}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="card2">
        {sel || isNew ? (
          <Editor
            key={sel ? `${sel.id}-${sel.version}` : 'new'}
            t={sel}
            canEdit={canEdit}
            onSaved={(r) => {
              // A new version may keep the id or get a new one; either way the list shows the latest.
              load.setData((l) => [...(l ?? []).filter((x) => x.id !== r.id && x.id !== sel?.id), r]);
              if (!sel || sel.id !== r.id) navigate(href.setup('missions', r.id), { replace: true });
            }}
          />
        ) : (
          <div className="state empty">{selectedId && load.data ? 'That template no longer exists.' : 'Pick a template to view or edit its steps.'}</div>
        )}
      </div>
    </div>
  );
}
